import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
    Box,
    Grid,
    ListItem,
    List,
    ListItemText
} from '@mui/material';
import { Patient } from '../../models/core';
import { Insurance } from '../../models/insurance';
import InsuranceList from './2_0/insurance/InsuranceList';

interface OutreachCreateConfirmDialogProps {
    open: boolean;
    patient:Patient;
    zcmId:number;
    insurance:Insurance[];
    onClose: () => void;
    onConfirm: (patient:Patient, insurance:Insurance[]) => Promise<void>;
}

const OutreachCreateConfirmDialog: React.FC<OutreachCreateConfirmDialogProps> = ({
    open,
    patient,
    zcmId,
    insurance,
    onClose,
    onConfirm,
}) => {
    const [isCreating, setIsCreating] = React.useState<boolean>(false);

    const handleConfirm = async () => {
        setIsCreating(true);
        await onConfirm(patient, insurance);
        setIsCreating(false);
    };

    const pad = (n: number) => n.toString().padStart(2, '0');
    const getDOB = (dobString:string) => {
        const date = new Date(dobString||'');
        return `${pad(date.getMonth()+1)}/${pad(date.getDate())}/${date.getFullYear()}`
    }

    return (
        <>
        <Dialog open={open} onClose={onClose} fullWidth
            PaperProps={{sx:{ maxWidth: '45%', minWidth:'500px' }}}>
            <DialogTitle><b>{'Attention'}</b></DialogTitle>
                <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                        {'You are about to create '}<b>{`${patient?.lastName}, ${patient?.firstName}`}</b>
                        {` in Ziphy Outreach.`}<br/>{`This patient will be linked with Medical Patient: ${zcmId}.`}
                </Typography>
                <Box
                    sx={{
                        border: '1px solid #e0e0e0', borderRadius: '8px',
                        p: 2, maxHeight: '60vh', overflowY: 'auto', margin:3
                    }}
                >
                    <Box sx={{ ml:1, mr:1, mt: 1, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Details</Typography>
                        <Grid container>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>DOB:</b> {getDOB(patient?.dob||'')}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Gender:</b> {patient?.gender}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Language:</b> {patient?.language}</Typography>
                            </Grid>
                            <Grid item xs={12}><Box height={'0.5em'}/></Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Phone:</b> {patient?.phone||''}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Alt Phone:</b> {patient?.phone2}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Email:</b> {patient?.email}</Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ mt: 1, mb: 3 }}>
                    <Typography variant="h6" ml={1}>Addresses</Typography>
                        <List>
                            <ListItem key={'addr-main'}>
                                <ListItemText
                                    primary={`${patient?.address || ''}${patient?.address2 ? ', ' + patient?.address2 : ''}`}
                                    secondary={`${patient?.city}, ${patient?.state} ${patient?.zip}`}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={{ mt: 1, mb: 3 }}>
                        <InsuranceList title='Insurance' readonly={true} controlled={true} insurance={insurance}/>
                    </Box>
                </Box>
            <Grid container><Grid item xs={12}><Box height={'0.25em'}/></Grid></Grid>
                <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                    {'This cannot be undone.'}
                </Typography>
                <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                    {'Do you want to continue?'}
                </Typography>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={isCreating}
                    startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    Create Patient
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default OutreachCreateConfirmDialog;